import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { DrupalOAuth, fetchUserData } from "@parallelpublicworks/clf-utils";
import { generalSettings } from "@parallelpublicworks/gatsby-clf/gatsby-options-index";

const initialState = {
	isAuthenticated: null,
	clientId: null,
	baseUrl: null,
};

export const AuthContext = React.createContext(initialState);

const reducer = (state, action) => {
	switch (action?.type) {
		case "AUTHENTICATE_USER":
			return {
				...state,
				...action.payload,
				isAuthenticated: true,
			};
		case "LOGOUT_USER":
			return {
				...state,
				...action.payload,
				isAuthenticated: false,
			};
		default:
			return state;
	}
};

export const AuthContextProvider = (props) => {
	const auth = new DrupalOAuth(props);
	const [authState, dispatch] = React.useReducer(reducer, initialState);
	const { isAuthenticated } = authState;
	const [userData, setUserData] = useState(null);
	const isBrowser = typeof window !== "undefined";

	// Use refs instead of state for interval tracking to avoid re-renders
	const intervalRef = useRef(null);
	const isIntervalSetRef = useRef(false);

	// Use a ref to store the latest version of checkAuthStatus
	const checkAuthStatusRef = useRef(null);

	// Check if user is logged in and token is valid
	const checkAuthStatus = useCallback(async () => {
		const isLoggedIn = auth.isLoggedIn();
		const isTokenValid = auth.isTokenValid ? auth.isTokenValid() : isLoggedIn;
		console.warn(
			"...checkAuthStatus",
			"isLoggedIn",
			isLoggedIn,
			"isTokenValid",
			isTokenValid
		);

		if (isLoggedIn && !isTokenValid) {
			// Token exists but is invalid, try to refresh
			const refreshed = await auth.checkAndRefreshToken();

			if (refreshed) {
				// Token was successfully refreshed
				dispatch({
					type: "AUTHENTICATE_USER",
					payload: {
						isAuthenticated: true,
						clientId: auth.getClientId(),
						baseUrl: auth.getBaseUrl(),
					},
				});
			} else {
				// Token refresh failed, user is no longer authenticated
				await auth.logoutUser();
				dispatch({
					type: "LOGOUT_USER",
					payload: {
						isAuthenticated: false,
						clientId: null,
						baseUrl: null,
					},
				});
			}
		} else if (isLoggedIn && isTokenValid) {
			// User is logged in and token is valid
			dispatch({
				type: "AUTHENTICATE_USER",
				payload: {
					isAuthenticated: true,
					clientId: auth.getClientId(),
					baseUrl: auth.getBaseUrl(),
				},
			});
		} else {
			// User is not logged in
			dispatch({
				type: "LOGOUT_USER",
				payload: {
					isAuthenticated: false,
					clientId: null,
					baseUrl: null,
				},
			});
		}
	}, [auth]);

	// Update the ref whenever checkAuthStatus changes
	useEffect(() => {
		checkAuthStatusRef.current = checkAuthStatus;
	}, [checkAuthStatus]);

	// Initial auth check
	useEffect(() => {
		checkAuthStatus();
	}, []);

	// Set up periodic auth check - only when authentication status changes
	useEffect(() => {
		// Clear any existing interval first to prevent duplicates
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
			isIntervalSetRef.current = false;
		}

		// Only set up interval if authenticated and not already set
		if (isAuthenticated && !isIntervalSetRef.current) {
			// Check auth status every 2 minutes
			intervalRef.current = setInterval(() => {
				if (checkAuthStatusRef.current) {
					checkAuthStatusRef.current();
				}
			}, 120 * 1000);
			isIntervalSetRef.current = true;
		}

		// Cleanup function
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
				isIntervalSetRef.current = false;
			}
		};
	}, [isAuthenticated]);

	// Only depend on authentication status

	useEffect(() => {
		console.warn(
			"Authentication or location status changed, isAuthenticated?",
			isAuthenticated
		);

		if (!props.location.pathname.includes("oauth/") && isAuthenticated) {
			if (userData == null) {
				(async function () {
					let _userData = await fetchUserData(dispatch);
					setUserData(_userData);
					dispatch({
						type: "AUTHENTICATE_USER",
						payload: {
							isAuthenticated: true,
							clientId: auth.getClientId(),
							baseUrl: auth.getBaseUrl(),
						},
					});
				})();
			}
		} else if (!isAuthenticated && userData !== null) {
			if (typeof props.setGlobalAlert !== "undefined") {
				props.setGlobalAlert({
					message: "Your session has expired. Please log in again.",
					severity: "error",
				});
			}
			setUserData(null);
			dispatch({
				type: "LOGOUT_USER",
				payload: {
					...authState,
					isAuthenticated: false,
				},
			});
		}
	}, [authState, props.location]);

	// Add a logout function to the context
	const contextValue = useMemo(() => {
		if (!isBrowser) {
			return [
				{
					isAuthenticated: false,
					clientId: null,
					baseUrl: null,
				},
				{
					dispatch: () => {},
					logout: async () => {},
					auth0Logout: () => {},
					checkAuth: async () => {},
					isLoggedIn: () => false,
				},
				[userData, setUserData],
			];
		}

		return [
			authState,
			{
				dispatch,
				logout: async () => {
					await auth.logoutUser();
					dispatch({
						type: "LOGOUT_USER",
						payload: {
							isAuthenticated: false,
							clientId: null,
							baseUrl: null,
						},
					});
				},
				auth0Logout: () => {
					const logoutUrl = new URL(generalSettings.data.siteMetaData.drupalUrl);
					logoutUrl.pathname = "/understory-oauth/logout";
					window.location.replace(logoutUrl.toString());
				},
				checkAuth: checkAuthStatus,
				isLoggedIn: auth.isLoggedIn,
			},
			[userData, setUserData],
		];
	}, [authState, dispatch, auth, checkAuthStatus, userData, setUserData]);

	return (
		<AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>
	);
};
